import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import MobileFormDialog from "../../../components/ui/mobile-form-dialog";
import DocumentsManager from "../../../components/DocumentsManager";
import ComponentsManager from "../../../components/ComponentsManager";
import { FileCheck, Radio, Flame, Heart, Compass } from "lucide-react";
import { API } from "../../../App";


export const STATUSES = [
  { value: "ativa", label: "Ativa" },
  { value: "manutencao", label: "Em manutenção" },
  { value: "retirada", label: "Retirada" },
];

export const DOC_FIELDS = [
  { key: "airworthiness_valid_until",     label: "Navegabilidade",    icon: FileCheck },
  { key: "radio_license_valid_until",     label: "Lic. Rádio",        icon: Radio },
  { key: "extinguisher_valid_until",      label: "Extintor",           icon: Flame },
  { key: "first_aid_valid_until",         label: "1ºs Socorros",       icon: Heart },
  { key: "compass_calibration_valid_until", label: "Bússola",          icon: Compass },
];

// Cores: cinzento = sem registo, vermelho = já vencido, âmbar (`warn`) = válido mas a expirar, verde = válido.
export function DocChip({ label, ok, warn, valid, days, missing, icon: Icon }) {
  const cls = missing
    ? "border-slate-700 bg-slate-800/30 text-slate-500"
    : !ok
      ? "border-red-500/30 bg-red-500/10 text-red-200"
      : warn
        ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
        : "border-emerald-500/20 bg-emerald-500/5 text-emerald-200";
  return (
    <div className={`px-1.5 py-0.5 rounded border ${cls}`}>
      <div className="text-[9px] uppercase opacity-70 flex items-center gap-0.5">
        {Icon && <Icon className="w-2.5 h-2.5" />}
        {label}
      </div>
      <div className="font-mono text-[10px]">{valid || "—"}</div>
      {days != null && <div className="text-[9px] opacity-80">{days < 0 ? `${-days}d atrasado` : `${days}d`}</div>}
    </div>
  );
}

export default function AircraftForm({ aircraft, onClose, onSaved }) {
  const isEdit = !!aircraft?.id;
  const [form, setForm] = useState(() => ({
    registration: aircraft?.registration || "",
    model: aircraft?.model || "",
    classe: aircraft?.classe || "",
    status: aircraft?.status || "ativa",
    training_aircraft: aircraft?.training_aircraft !== false,
    notes: aircraft?.notes || "",
    initial_hours_minutes: aircraft?.initial_hours_minutes ?? 0,
    documents: { ...(aircraft?.documents || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!form.registration.trim() || !form.model.trim()) { setError("Matrícula e modelo são obrigatórios."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        registration: form.registration.trim().toUpperCase(),
        model: form.model.trim(),
        classe: form.classe || null,
        notes: form.notes?.trim() || "",
        initial_hours_minutes: Number(form.initial_hours_minutes) || 0,
        documents: Object.fromEntries(Object.entries(form.documents).map(([k, v]) => [k, v || null])),
      };
      if (isEdit) {
        await axios.put(`${API}/di/aircraft/${aircraft.id}`, payload, { withCredentials: true });
        toast.success("Aeronave atualizada");
      } else {
        await axios.post(`${API}/di/aircraft`, payload, { withCredentials: true });
        toast.success("Aeronave criada");
      }
      onSaved();
    } catch (e) { const d = e?.response?.data?.detail; setError(typeof d === "string" ? d : "Erro a guardar"); }
    finally { setSaving(false); }
  };

  return (
    <MobileFormDialog open onOpenChange={(o) => { if (!o) onClose(); }} title={isEdit ? `Editar ${aircraft.registration}` : "Nova aeronave"}
      description="Os documentos alimentam o estado operacional e os alertas. Os prazos de manutenção registam-se em Manutenções."
      size="xl" onSubmit={submit} submitLabel={isEdit ? "Atualizar" : "Criar"} submitting={saving} testId="di-ac-form-dialog">
      <div className="space-y-4 text-xs">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="text-slate-300">Matrícula *</Label><Input value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} placeholder="CS-XYZ" className="bg-slate-800 border-slate-700" data-testid="di-ac-form-registration" /></div>
          <div><Label className="text-slate-300">Modelo *</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Tecnam P92" className="bg-slate-800 border-slate-700" data-testid="di-ac-form-model" /></div>
          <div>
            <Label className="text-slate-300">Classe *</Label>
            <select
              value={form.classe}
              onChange={(e) => setForm({ ...form, classe: e.target.value })}
              className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-sm text-slate-100"
              data-testid="di-ac-form-classe"
            >
              <option value="">Por classificar (não conta)</option>
              <option value="ultraleve">Ultraleve (conta para a experiência)</option>
              <option value="ligeiro">Ligeiro</option>
              <option value="experimental">Experimental</option>
            </select>
            <p className="text-[11px] text-slate-500 mt-1">Só as aeronaves ultraleve contam para as horas do piloto e do instrutor.</p>
          </div>
          <div>
            <Label className="text-slate-300">Estado</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="di-ac-form-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 border border-cockpit-border rounded p-2.5">
          <input
            type="checkbox"
            id="training_aircraft"
            checked={form.training_aircraft}
            onChange={(e) => setForm({ ...form, training_aircraft: e.target.checked })}
            className="h-4 w-4 rounded border-slate-600 bg-slate-800 accent-orange-400"
            data-testid="di-ac-form-training-aircraft"
          />
          <label htmlFor="training_aircraft" className="text-sm text-slate-200 cursor-pointer">
            Aeronave afecta à formação
          </label>
          <span className="text-[10px] text-slate-500 ml-1">(incluída nos relatórios ANAC)</span>
        </div>
        <div className="border border-cockpit-border rounded p-2">
          <div className="text-slate-300 font-semibold mb-2">Validades de documentos</div>
          {(aircraft?.initial_hours_minutes > 0) && (
            <div className="mb-3 text-[11px] text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded p-2">
              ⚠ Este avião tem <strong>{Math.floor(aircraft.initial_hours_minutes / 60)}:{String(aircraft.initial_hours_minutes % 60).padStart(2,'0')}</strong> de horas iniciais definidas (campo antigo).
              Mova esse valor para o campo <strong>Horas prévias</strong> do componente <strong>Célula</strong> ou <strong>Motor</strong> conforme apropriado.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {DOC_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-slate-400 text-[11px] flex items-center gap-1">
                  {f.icon && <f.icon className="w-3 h-3 opacity-70" />}
                  {f.label}
                </Label>
                <Input type="date" value={form.documents[f.key] || ""} onChange={(e) => setForm({ ...form, documents: { ...form.documents, [f.key]: e.target.value } })} className="bg-slate-800 border-slate-700 w-full min-w-0" data-testid={`di-ac-form-doc-${f.key}`} />
              </div>
            ))}
          </div>
        </div>
        {isEdit && <DocumentsManager ownerKind="aircraft" ownerId={aircraft.id} />}
        {!isEdit && <div className="border border-dashed border-slate-700 rounded p-3 text-[11px] text-slate-500 italic">Cópias de documentos podem ser carregadas depois de criar a aeronave.</div>}
        <div><Label className="text-slate-300">Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-slate-800 border-slate-700" data-testid="di-ac-form-notes" /></div>
        {isEdit && <ComponentsManager aircraftId={aircraft.id} registration={aircraft.registration} />}
        {error && <div className="text-red-400 text-[11px]">{error}</div>}
      </div>
    </MobileFormDialog>
  );
}
